export type DietaryTag = 'gf' | 'v' | 'vg' | 'df' | 'n' | 'spicy' | 'raw' | 'halal' | 'kosher'

export interface DietaryInfo {
  tag: DietaryTag
  label: string
  description: string
  icon?: string
}

export const DIETARY_LABELS: Record<DietaryTag, DietaryInfo> = {
  gf: { tag: 'gf', label: 'Gluten-Free', description: 'Contains no gluten' },
  v: { tag: 'v', label: 'Vegetarian', description: 'No meat or fish' },
  vg: { tag: 'vg', label: 'Vegan', description: 'No animal products' },
  df: { tag: 'df', label: 'Dairy-Free', description: 'Contains no dairy' },
  n: { tag: 'n', label: 'Contains Nuts', description: 'Contains tree nuts or peanuts' },
  spicy: { tag: 'spicy', label: 'Spicy', description: 'Contains hot spices' },
  raw: { tag: 'raw', label: 'Raw', description: 'Contains raw ingredients' },
  halal: { tag: 'halal', label: 'Halal', description: 'Halal certified' },
  kosher: { tag: 'kosher', label: 'Kosher', description: 'Kosher certified' },
}

export interface Modifier {
  id: string
  name: string
  priceDeltaCents: number
  available: boolean
  default?: boolean
  calories?: number
  nestedGroups?: ModifierGroup[]
}

export interface ModifierGroup {
  id: string
  name: string
  description?: string
  required: boolean
  minSelections: number
  maxSelections: number
  modifiers: Modifier[]
}

export interface MenuItem {
  id: string
  categoryId: string
  name: string
  description: string
  priceCents: number
  image?: string
  dietaryTags: DietaryTag[]
  available: boolean
  modifierGroups: ModifierGroup[]
  featured?: boolean
  popular?: boolean
  new?: boolean
  calories?: number
  prepTimeMinutes?: number
  sortOrder: number
  hidden?: boolean
}

export interface MenuCategory {
  id: string
  name: string
  description?: string
  image?: string
  displayOrder: number
  items: MenuItem[]
  hidden?: boolean
  scheduledOnly?: boolean
  availableFrom?: string
  availableUntil?: string
}

export interface Menu {
  tenantId: string
  categories: MenuCategory[]
  lastUpdated: string
}

export interface SelectedModifier {
  groupId: string
  modifierId: string
  modifier: Modifier
}

export interface CartItem {
  id: string
  menuItemId: string
  menuItem: MenuItem
  quantity: number
  selectedModifiers: SelectedModifier[]
  specialInstructions?: string
  lineTotalCents: number
}

export function calculateItemPrice(item: MenuItem, modifiers: SelectedModifier[]): number {
  const basePrice = item.priceCents
  const modifierTotal = modifiers.reduce((sum, m) => sum + m.modifier.priceDeltaCents, 0)
  return basePrice + modifierTotal
}

export function calculateLineTotal(item: MenuItem, modifiers: SelectedModifier[], quantity: number): number {
  return calculateItemPrice(item, modifiers) * quantity
}
